import { useCallback, useMemo } from 'react';
import { useUserInfo } from 'academy-common-lib/features/accounts/accountsQueries';
import { useRotatingThinkingLabel } from '../../hooks/useRotatingThinkingLabel';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { ChatView } from 'breeze-engine-chat-components/ChatView';
import { SectionContext } from 'ai-rendering-library/context/SectionContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { GradientCapsIcon } from '../GradientCapsIcon';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import I18n from 'I18n';
import { AcademyChatActionsProvider } from '../../context/AcademyChatActionsContext';
import { ChatInputArea } from './ChatInputArea';
import { SuggestedLearningPrompts } from './SuggestedLearningPrompts';
import { ChatErrorState } from './ChatErrorState';
import { ACADEMY_BLOCK_RENDERERS } from './AcademyBlockRenderers';
import { ToolCallProgress } from './ToolCallProgress';
import { getVisibleChatMessages } from '../../utils/getVisibleChatMessages';
import { sendMessageWithContext } from '../../utils/sendMessageWithContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const noop = () => {};
const SECTION_CONTEXT_VALUE = {
  getIsUngated: () => true,
  getHasScope: () => false,
  disableThoughts: true,
  isWaitingForChatResponse: false,
  layoutSize: 'LG',
  onCopilotActionClicked: noop,
  onCopilotActionV2Clicked: noop,
  onLinkActionClicked: noop,
  onTemplateActionClicked: noop,
  onThoughtsClicked: noop
};
export const ChatMainView = ({
  manager,
  inFlightTools,
  onMessageSent,
  trackEvent,
  onContentCardClick,
  onContentCardImpression,
  onExploreMoreClick,
  onPlaylistStartClick,
  onPlaylistManageClick,
  onPlaylistSuggested,
  onResponseStopped,
  onStartNewChat,
  isOnboardingFlow
}) => {
  var _active$messages$leng, _active$messages2;
  const {
    userInfo
  } = useUserInfo();
  const firstName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.firstName;
  const active = manager.active;
  const messages = useMemo(() => {
    var _active$messages;
    return (_active$messages = active === null || active === void 0 ? void 0 : active.messages) !== null && _active$messages !== void 0 ? _active$messages : [];
  }, [active === null || active === void 0 ? void 0 : active.messages]);
  const hasMessages = ((_active$messages$leng = active === null || active === void 0 || (_active$messages2 = active.messages) === null || _active$messages2 === void 0 ? void 0 : _active$messages2.length) !== null && _active$messages$leng !== void 0 ? _active$messages$leng : 0) > 0;
  const visibleMessages = useMemo(() => getVisibleChatMessages(messages), [messages]);
  const {
    scrollContainerRef,
    transcriptRef,
    spacerRef,
    isPinHeld,
    resumeDirection,
    resumePin
  } = useAutoScroll({
    messages: visibleMessages,
    conversationId: manager.activeConversationId
  });
  const isLoading = active === null || active.status === 'LOADING';
  const hasError = !isLoading && (active === null || active === void 0 ? void 0 : active.status) === 'ERROR' && (active === null || active === void 0 ? void 0 : active.error);
  const isStreaming = active !== null && (active.status === 'STREAMING' || active.status === 'AWAITING_TOOL' || active.status === 'AWAITING_APPROVAL');
  const isShowingThinkingLabel = isLoading || isStreaming && inFlightTools.length === 0;
  const rotatingThinkingLabel = useRotatingThinkingLabel(isShowingThinkingLabel);
  const handleSend = useCallback((text, appearsIn = 'text-input') => {
    if (!text.trim()) return;
    onMessageSent(appearsIn);
    sendMessageWithContext(manager, text).catch(Raven.captureException.bind(Raven));
  }, [manager, onMessageSent]);
  const handleStop = useCallback(() => {
    onResponseStopped();
    manager.stop().catch(Raven.captureException.bind(Raven));
  }, [manager, onResponseStopped]);
  const submitFeedback = useCallback(options => manager.submitFeedback(options), [manager]);
  const chatActions = useMemo(() => ({
    onSuggestionClick: text => handleSend(text, 'suggestion-chip'),
    isStreaming,
    isOnboardingFlow,
    trackEvent,
    onContentCardClick,
    onContentCardImpression,
    onExploreMoreClick,
    onPlaylistStartClick,
    onPlaylistManageClick,
    onPlaylistSuggested,
    submitFeedback
  }), [handleSend, isStreaming, isOnboardingFlow, trackEvent, onContentCardClick, onContentCardImpression, onExploreMoreClick, onPlaylistStartClick, onPlaylistManageClick, onPlaylistSuggested, submitFeedback]);
  return /*#__PURE__*/_jsx(AcademyChatActionsProvider, {
    value: chatActions,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      flex: 1,
      blockSize: "100%",
      children: isLoading ? /*#__PURE__*/_jsx(View, {
        flex: 1,
        overflow: "hidden",
        paddingInline: "600",
        paddingBlock: "300",
        children: /*#__PURE__*/_jsx(View, {
          maxInlineSize: "800px",
          inlineSize: "100%",
          marginInline: "auto",
          children: /*#__PURE__*/_jsx(ToolCallProgress, {
            fallbackLabel: rotatingThinkingLabel
          })
        })
      }) : hasError ? /*#__PURE__*/_jsx(ChatErrorState, {
        onStartNewChat: onStartNewChat
      }) : !hasMessages ? /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        flex: 1,
        paddingInline: "600",
        paddingBlock: "300",
        children: [/*#__PURE__*/_jsx(View, {
          maxInlineSize: "800px",
          inlineSize: "100%",
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "400",
            children: [/*#__PURE__*/_jsx(GradientCapsIcon, {
              size: 48
            }), /*#__PURE__*/_jsx(Text, {
              typography: "heading-500",
              children: firstName ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academy-ai-assistant.chat.emptyState.greeting",
                options: {
                  firstName
                }
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academy-ai-assistant.chat.emptyState.greetingFallback"
              })
            })]
          })
        }), /*#__PURE__*/_jsxs(View, {
          maxInlineSize: "800px",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(SuggestedLearningPrompts, {
            onSelect: text => handleSend(text, 'suggestion-chip')
          }), /*#__PURE__*/_jsx(View, {
            paddingBlockStart: "600",
            children: /*#__PURE__*/_jsx(ChatInputArea, {
              autoFocus: true,
              isStreaming: isStreaming,
              onSend: handleSend,
              onStop: handleStop
            })
          })]
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(View, {
          position: "relative",
          flex: 1,
          children: [/*#__PURE__*/_jsxs(View, {
            position: "absolute",
            inset: "0",
            overflow: "auto",
            paddingInline: "600",
            paddingBlock: "300",
            ref: scrollContainerRef,
            children: [/*#__PURE__*/_jsxs(View, {
              maxInlineSize: "800px",
              inlineSize: "100%",
              marginInline: "auto",
              ref: transcriptRef,
              children: [/*#__PURE__*/_jsx(SectionContext.Provider, {
                value: SECTION_CONTEXT_VALUE,
                children: /*#__PURE__*/_jsx(ChatView, {
                  messages: visibleMessages,
                  components: ACADEMY_BLOCK_RENDERERS
                })
              }), isStreaming && /*#__PURE__*/_jsx(Flex, {
                paddingBlockStart: "400",
                children: /*#__PURE__*/_jsx(ToolCallProgress, {
                  tools: inFlightTools,
                  fallbackLabel: rotatingThinkingLabel
                })
              })]
            }), /*#__PURE__*/_jsx(View, {
              ref: spacerRef,
              "aria-hidden": true
            })]
          }), isPinHeld === false && /*#__PURE__*/_jsx(View, {
            position: "absolute",
            insetBlockEnd: "400",
            insetInlineEnd: "600",
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text('academy-ai-assistant.chat.jumpToReply'),
              onClick: resumePin,
              shape: "circle",
              use: "secondary",
              size: "sm",
              children: resumeDirection === 'up' ? /*#__PURE__*/_jsx(UpIcon, {
                size: "500"
              }) : /*#__PURE__*/_jsx(DownIcon, {
                size: "500"
              })
            })
          })]
        }), /*#__PURE__*/_jsx(View, {
          paddingInline: "600",
          paddingBlock: "300",
          borderBlockStartWidth: "100",
          borderColor: "border-core-default",
          children: /*#__PURE__*/_jsx(View, {
            maxInlineSize: "800px",
            inlineSize: "100%",
            marginInline: "auto",
            children: /*#__PURE__*/_jsx(ChatInputArea, {
              isStreaming: isStreaming,
              onSend: handleSend,
              onStop: handleStop
            })
          })
        })]
      })
    })
  });
};